import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier accent strip — Coral is reserved for the recommended tier (growth).
// Other tiers use Deep Pine, Sand-muted, or a darker Pine outline so the
// recommended one truly stands out.
const TIER_VARIANT = {
  starter:    { stripe: 'bg-[var(--vh-text-muted)]',    cta: 'Get started free', tone: 'ghost' },
  growth:     { stripe: 'bg-[var(--vh-accent)]',        cta: 'Upgrade to Growth', tone: 'accent' },
  scale:      { stripe: 'bg-[var(--vh-brand-primary)]', cta: 'Upgrade to Scale',  tone: 'primary' },
  enterprise: { stripe: 'bg-[var(--vh-text-primary)]',  cta: 'Contact sales',     tone: 'primary' },
};

function formatGBP(n) {
  if (n === 0 || n === 0.0) return 'Free';
  if (n == null) return 'Custom';
  return `£${n.toFixed(n % 1 === 0 ? 0 : 2)}`;
}

function PublicTopBar() {
  return (
    <header className="border-b border-[var(--vh-border)] bg-[var(--vh-bg-base)]/85 backdrop-blur sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display text-xl tracking-tight text-[var(--vh-brand-primary)]" data-testid="pricing-brand">
          <span className="inline-flex w-8 h-8 rounded-md bg-[var(--vh-brand-primary)] text-white items-center justify-center text-sm font-bold">V</span>
          Vault Hire
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm text-[var(--vh-text-secondary)]">
          <Link to="/jobs" className="hover:text-[var(--vh-brand-primary)]">Jobs board</Link>
          <Link to="/companies" className="hover:text-[var(--vh-brand-primary)]">Companies</Link>
          <Link to="/trust" className="hover:text-[var(--vh-brand-primary)]">Trust</Link>
          <Link to="/pricing" className="text-[var(--vh-brand-primary)] font-semibold">Pricing</Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="px-4 py-2 rounded-full text-sm text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)]">Sign in</Link>
          <Link to="/register" className="px-4 py-2 rounded-full text-sm bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)]">Get started</Link>
        </div>
      </div>
    </header>
  );
}

export default function PricingPage({ user }) {
  const [plans, setPlans] = useState(null);
  const [billing, setBilling] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [loadingTier, setLoadingTier] = useState(null);
  const [loadingAddon, setLoadingAddon] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/pricing/plans`)
      .then((r) => setPlans(r.data))
      .catch(() => setError('Could not load pricing.'));
    if (user) {
      axios.get(`${API}/me/billing`).then((r) => setBilling(r.data)).catch(() => {});
    }
  }, [user]);

  async function checkoutTier(tier_id) {
    if (!user) { navigate(`/login?redirect=/pricing`); return; }
    if (tier_id === 'starter') { navigate('/'); return; }
    if (tier_id === 'enterprise') {
      window.location.href = 'mailto:sales@vault-hire.com?subject=Enterprise%20enquiry';
      return;
    }
    setLoadingTier(tier_id); setError('');
    try {
      const { data } = await axios.post(`${API}/payments/checkout/tier`, {
        tier_id, period, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Checkout failed.');
      setLoadingTier(null);
    }
  }

  async function checkoutAddon(package_id) {
    if (!user) { navigate(`/login?redirect=/pricing`); return; }
    setLoadingAddon(package_id); setError('');
    try {
      const { data } = await axios.post(`${API}/payments/checkout/payg`, {
        package_id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Checkout failed.');
      setLoadingAddon(null);
    }
  }

  if (!plans) {
    return (
      <div className="min-h-screen bg-[var(--vh-bg-base)] flex items-center justify-center" data-testid="pricing-page-loading">
        <div className="text-[var(--vh-text-secondary)]">Loading pricing…</div>
      </div>
    );
  }

  const priceFor = (tier) => {
    if (tier.monthly_price === 0) return { display: 'Free', sub: 'forever' };
    if (tier.monthly_price == null) return { display: 'Custom', sub: 'contact sales' };
    if (period === 'monthly') return { display: formatGBP(tier.monthly_price), sub: '/ month' };
    const annual = tier.monthly_price * 12 * (1 - plans.annual_discount_pct / 100);
    return { display: formatGBP(annual), sub: `/ year (save ${plans.annual_discount_pct}%)` };
  };

  return (
    <div className="min-h-screen bg-[var(--vh-bg-base)] text-[var(--vh-text-primary)]" data-testid="pricing-page">
      <PublicTopBar />

      <section className="max-w-6xl mx-auto px-6 pt-20 pb-12 text-center">
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-4" data-testid="pricing-eyebrow">Pricing</p>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.04] mb-5" data-testid="pricing-title">
          Built for hiring teams.<br/>
          <span className="text-[var(--vh-brand-primary)]">Free where it matters.</span>
        </h1>
        <p className="text-lg text-[var(--vh-text-secondary)] max-w-2xl mx-auto leading-relaxed">
          Responding to reference requests is <strong className="text-[var(--vh-text-primary)]">always free</strong>. Pay only when you actively hire.
        </p>

        {billing && (
          <div className="mt-7 inline-block px-4 py-2 rounded-full bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] text-sm text-[var(--vh-text-secondary)]" data-testid="current-plan-pill">
            Current plan: <strong className="text-[var(--vh-text-primary)]">{billing.subscription.tier_name}</strong>
            {billing.reference_credits > 0 && (
              <> · <strong className="text-[var(--vh-text-primary)]">{billing.reference_credits}</strong> credit{billing.reference_credits === 1 ? '' : 's'}</>
            )}
          </div>
        )}

        <div className="mt-8 inline-flex p-1 rounded-full bg-[var(--vh-bg-surface)] border border-[var(--vh-border)]" data-testid="period-toggle">
          {['monthly', 'annual'].map((p) => (
            <button
              key={p}
              data-testid={`period-${p}-btn`}
              onClick={() => setPeriod(p)}
              className={`px-5 py-2 rounded-full text-sm capitalize transition ${period === p ? 'bg-[var(--vh-brand-primary)] text-white font-semibold' : 'text-[var(--vh-text-secondary)] hover:text-[var(--vh-brand-primary)]'}`}
            >
              {p}{p === 'annual' && <span className="ml-2 text-[var(--vh-accent)] font-semibold">−{plans.annual_discount_pct}%</span>}
            </button>
          ))}
        </div>

        {error && <div className="mt-4 text-sm text-[var(--vh-accent)]" data-testid="pricing-error">{error}</div>}
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.tiers.map((tier) => {
            const variant = TIER_VARIANT[tier.id] || TIER_VARIANT.starter;
            const { display, sub } = priceFor(tier);
            const isCurrent = billing?.subscription?.tier_id === tier.id;
            const isPopular = tier.id === 'growth';
            const ctaClass =
              variant.tone === 'accent'
                ? 'bg-[var(--vh-accent)] text-white hover:bg-[var(--vh-accent-hover)]'
                : variant.tone === 'primary'
                ? 'bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)]'
                : 'bg-[var(--vh-bg-base)] border border-[var(--vh-border)] text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)]';
            return (
              <div
                key={tier.id}
                data-testid={`tier-card-${tier.id}`}
                className={`relative overflow-hidden rounded-3xl bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] p-7 flex flex-col ${isPopular ? 'lg:scale-105 lg:-translate-y-2 shadow-xl border-[var(--vh-accent)]' : 'shadow-sm'}`}
              >
                <span className={`absolute inset-x-0 top-0 h-1 ${variant.stripe}`} aria-hidden="true" />
                {isPopular && (
                  <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.18em] text-white bg-[var(--vh-accent)] px-2.5 py-1 rounded-full" data-testid="popular-badge">Most popular</div>
                )}
                <h3 className="font-display text-2xl text-[var(--vh-text-primary)] mt-2">{tier.name}</h3>
                <div className="mt-5 mb-1">
                  <span className="font-display text-4xl text-[var(--vh-text-primary)]" data-testid={`price-${tier.id}`}>{display}</span>
                  {sub && <span className="ml-2 text-sm text-[var(--vh-text-muted)]">{sub}</span>}
                </div>
                <ul className="text-sm text-[var(--vh-text-secondary)] space-y-2 mt-5 flex-1">
                  {tier.features.map((f) => (
                    <li key={`${tier.id}-${f}`} className="flex gap-2">
                      <span className="text-[var(--vh-accent)] mt-0.5">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`checkout-${tier.id}-btn`}
                  onClick={() => checkoutTier(tier.id)}
                  disabled={loadingTier === tier.id || isCurrent}
                  className={`mt-6 w-full py-3 rounded-full text-sm font-semibold transition disabled:opacity-50 ${ctaClass}`}
                >
                  {isCurrent ? 'Current plan' : (loadingTier === tier.id ? 'Redirecting…' : variant.cta)}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-3">Top-ups</p>
          <h2 className="font-display text-3xl md:text-4xl text-[var(--vh-text-primary)] mb-2">Need more references?</h2>
          <p className="text-[var(--vh-text-secondary)]">Top up your account or grab a credit pack — no subscription required.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {plans.addons.map((addon) => (
            <div key={addon.id} className="rounded-2xl p-7 bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] shadow-sm" data-testid={`addon-card-${addon.id}`}>
              <h4 className="font-display text-xl text-[var(--vh-text-primary)]">{addon.name}</h4>
              <p className="text-sm text-[var(--vh-text-secondary)] mt-1 mb-4 leading-relaxed">{addon.description}</p>
              <div className="flex items-baseline gap-2 mb-5">
                <span className="font-display text-3xl text-[var(--vh-text-primary)]" data-testid={`addon-price-${addon.id}`}>{formatGBP(addon.amount)}</span>
                <span className="text-[var(--vh-text-muted)] text-sm">one-time</span>
              </div>
              <button
                data-testid={`buy-${addon.id}-btn`}
                onClick={() => checkoutAddon(addon.id)}
                disabled={loadingAddon === addon.id}
                className="w-full py-3 rounded-full text-sm font-semibold bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)] transition disabled:opacity-50"
              >
                {loadingAddon === addon.id ? 'Redirecting…' : `Buy — ${formatGBP(addon.amount)}`}
              </button>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-[var(--vh-border)] bg-[var(--vh-bg-muted)] py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-xs text-[var(--vh-text-muted)]">
          Billed via Stripe · GBP · Cancel anytime · <Link to="/trust" className="underline hover:text-[var(--vh-brand-primary)]">Security &amp; compliance</Link>
        </div>
      </footer>
    </div>
  );
}
